'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { Lock, ChevronDown, X, Check } from 'lucide-react';
import LanguageSelector from '@/app/components/LanguageSelector';
import { BRAND_DISPLAY, BRAND_LOGO } from '@/lib/brand';
import {
  DISPLAY_CURRENCIES,
  currencyForLocale,
  formatDisplayAmount,
} from '@/lib/display-currency';

const CURRENCIES = DISPLAY_CURRENCIES;

function PricingCard({
  title,
  price,
  period,
  featuresList,
  btn,
  isFeatured,
  locale,
  priceState = 'ready',
  priceLoadingLabel,
  priceUnavailableLabel,
  ctaHref,
}) {
  const showPrice = priceState === 'ready';
  const showLoading = priceState === 'loading';

  return (
    <div
      className={`flex flex-col items-center rounded-xl p-3.5 transition-all md:p-4 ${
        isFeatured
          ? 'border border-cyan-500/50 bg-gradient-to-br from-cyan-900/50 to-slate-900 text-white shadow-[0_0_14px_rgba(6,182,212,0.1)]'
          : 'border border-slate-800 bg-slate-900/50 text-white'
      }`}
    >
      <h3
        className={`mb-2.5 text-[9px] font-black uppercase tracking-[0.18em] ${
          isFeatured ? 'text-cyan-400' : 'text-slate-500'
        }`}
      >
        {title}
      </h3>
      <div className="mb-3 flex min-h-[2rem] flex-col items-center justify-center">
        {showLoading ? (
          <span className="animate-pulse text-xs font-bold text-slate-400">
            {priceLoadingLabel}
          </span>
        ) : showPrice ? (
          <>
            <span className="text-xl font-light tracking-tight md:text-2xl">{price}</span>
            <span className="ml-1 text-[9px] font-bold uppercase opacity-50">{period}</span>
          </>
        ) : (
          <span className="px-2 text-center text-[11px] font-bold text-amber-300/90">
            {priceUnavailableLabel}
          </span>
        )}
      </div>

      <ul className="mb-3.5 w-full flex-1 space-y-1.5 text-left">
        {featuresList.map((feature) => (
          <li
            key={feature.text}
            className={`flex items-start gap-1.5 text-[11px] font-semibold leading-snug ${
              feature.included ? 'text-slate-300' : 'text-slate-600 opacity-60'
            }`}
          >
            <div
              className={`mt-0.5 shrink-0 ${
                feature.included
                  ? feature.highlight
                    ? 'text-cyan-400'
                    : 'text-slate-500'
                  : 'text-slate-700'
              }`}
            >
              {feature.included ? <Check size={11} /> : <X size={11} />}
            </div>
            <span className={feature.highlight ? 'text-cyan-300' : ''}>{feature.text}</span>
          </li>
        ))}
      </ul>

      <Link
        href={ctaHref || `/${locale}/register`}
        className={`w-full rounded-lg py-2 text-center text-[9px] font-black uppercase tracking-widest transition-all ${
          isFeatured
            ? 'bg-cyan-600 text-white shadow-md shadow-cyan-900/40 hover:bg-cyan-500'
            : 'bg-slate-800 text-white hover:bg-slate-700'
        }`}
      >
        {btn}
      </Link>
    </div>
  );
}

export default function LandingClient({ locale, i18n }) {
  const tPay = useTranslations('Pay');
  const isRtl = locale === 'ar';

  const [currency, setCurrency] = useState(() => currencyForLocale(locale).code);

  useEffect(() => {
    setCurrency(currencyForLocale(locale).code);
  }, [locale]);
  const [showCurrencyMenu, setShowCurrencyMenu] = useState(false);
  const [prices, setPrices] = useState({
    ELITE_MONTHLY: null,
    ELITE_ANNUAL: null,
  });
  const [pricesState, setPricesState] = useState('loading'); // loading | ready | unavailable

  const eliteFeatures = useMemo(
    () => [
      { text: i18n.pricing.elite.features.f0, included: true },
      { text: i18n.pricing.elite.features.f1, included: true, highlight: true },
      { text: i18n.pricing.elite.features.f2, included: true },
      { text: i18n.pricing.elite.features.f3, included: true }
    ],
    [i18n]
  );

  useEffect(() => {
    let cancelled = false;

    const planCop = (row) => {
      const fromCop = Number(row?.price_cop || 0);
      if (fromCop > 0) return fromCop;
      const cents = Number(row?.amount_in_cents || 0);
      return cents > 0 ? Math.round(cents / 100) : 0;
    };

    const fetchPricesOnce = async (signal) => {
      const response = await fetch('/api/plans', { cache: 'no-store', signal });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.success || !Array.isArray(payload.plans)) {
        throw new Error('PLAN_CATALOG_UNAVAILABLE');
      }

      const monthly = payload.plans.find(
        (p) => String(p.id).toLowerCase() === 'premium_monthly'
      );
      const annual = payload.plans.find(
        (p) => String(p.id).toLowerCase() === 'premium_annual'
      );
      const monthlyCop = planCop(monthly);
      const annualCop = planCop(annual);

      if (!monthlyCop || !annualCop) {
        throw new Error('PLAN_PRICE_MISSING');
      }

      return { ELITE_MONTHLY: monthlyCop, ELITE_ANNUAL: annualCop };
    };

    const fetchPrices = async () => {
      setPricesState('loading');
      let lastError = null;

      for (let attempt = 0; attempt < 2; attempt += 1) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000);
        try {
          const next = await fetchPricesOnce(controller.signal);
          if (!cancelled) {
            setPrices(next);
            setPricesState('ready');
          }
          clearTimeout(timeoutId);
          return;
        } catch (error) {
          lastError = error;
          clearTimeout(timeoutId);
        }
      }

      if (!cancelled) {
        console.warn('LANDING_PLAN_PRICES_FAILED', lastError?.message || lastError);
        setPrices({ ELITE_MONTHLY: null, ELITE_ANNUAL: null });
        setPricesState('unavailable');
      }
    };

    fetchPrices();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.selector-menu-container')) {
        setShowCurrencyMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <>
      {/* One compact top bar — not stretched to screen edges on wide monitors */}
      <nav className="fixed top-0 inset-x-0 z-50 border-b border-white/5 bg-[#020617]/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-2 px-3 py-3 sm:gap-3 sm:px-4">
          <Link
            href={`/${locale}`}
            className="flex min-w-0 shrink items-center rounded-lg bg-white px-1.5 py-0.5"
            aria-label={BRAND_DISPLAY}
          >
            <Image
              src={BRAND_LOGO}
              alt={BRAND_DISPLAY}
              width={320}
              height={178}
              unoptimized
              priority
              className="h-12 w-auto max-w-[16rem] object-contain sm:h-14 sm:max-w-[20rem]"
            />
          </Link>

          <div className="flex min-w-0 shrink items-center justify-end gap-1 sm:gap-1.5 md:gap-2">
            <LanguageSelector />

            <div className="relative selector-menu-container" data-tour="currency-selector">
              <button
                type="button"
                onClick={() => setShowCurrencyMenu((prev) => !prev)}
                className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 text-xs font-bold text-slate-200 transition-colors hover:bg-white/10 sm:px-2.5"
              >
                {currency} <ChevronDown size={14} />
              </button>
              {showCurrencyMenu && (
                <div className="absolute right-0 top-full z-50 mt-2 max-h-80 w-28 overflow-y-auto rounded-xl border border-white/10 bg-[#0F172A] shadow-2xl">
                  {CURRENCIES.map((currencyItem) => (
                    <button
                      key={currencyItem.code}
                      type="button"
                      onClick={() => {
                        setCurrency(currencyItem.code);
                        setShowCurrencyMenu(false);
                      }}
                      className="w-full border-b border-white/5 px-3 py-2.5 text-left text-xs font-medium text-slate-300 transition-colors last:border-0 hover:bg-cyan-500/20 hover:text-cyan-400"
                    >
                      {currencyItem.code}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Link
              href={`/${locale}/login`}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-cyan-500 px-2.5 py-1.5 text-xs font-bold text-[#020617] transition-all hover:bg-cyan-400 sm:px-4"
            >
              <Lock size={12} className="shrink-0" />
              <span className="max-[360px]:hidden">{i18n.loginButton}</span>
            </Link>
          </div>
        </div>
      </nav>

      <section id="pricing" dir={isRtl ? 'rtl' : 'ltr'} className="relative z-10 overflow-x-hidden border-t border-white/5 bg-[#0F172A] px-4 py-7 md:px-6 md:py-8">{/* i18n-guard-ignore: dir attr */}
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="mb-4 break-words text-lg font-black text-white sm:text-xl md:mb-5 md:text-2xl">{i18n.pricingTitle}</h2>

          <div className="grid items-stretch gap-2.5 sm:gap-3 lg:grid-cols-3">
            <PricingCard
              title={i18n.pricing.basic.title}
              price={formatDisplayAmount(0, currency)}
              period={i18n.pricing.basic.period}
              featuresList={i18n.pricing.basic.features}
              btn={i18n.pricing.basic.button}
              locale={locale}
              priceState="ready"
            />

            <PricingCard
              title={tPay('eliteMonthly')}
              price={
                prices.ELITE_MONTHLY != null
                  ? formatDisplayAmount(prices.ELITE_MONTHLY, currency)
                  : ''
              }
              period={tPay('perMonth')}
              isFeatured
              featuresList={eliteFeatures}
              btn={tPay('activate_elite')}
              locale={locale}
              priceState={pricesState}
              priceLoadingLabel={tPay('priceLoading')}
              priceUnavailableLabel={tPay('priceUnavailable')}
              ctaHref={`/${locale}/pay?cycle=monthly`}
            />

            <PricingCard
              title={tPay('eliteAnnual')}
              price={
                prices.ELITE_ANNUAL != null
                  ? formatDisplayAmount(prices.ELITE_ANNUAL, currency)
                  : ''
              }
              period={tPay('perYear')}
              featuresList={eliteFeatures}
              btn={tPay('activate_elite_annual')}
              locale={locale}
              priceState={pricesState}
              priceLoadingLabel={tPay('priceLoading')}
              priceUnavailableLabel={tPay('priceUnavailable')}
              ctaHref={`/${locale}/pay?cycle=annual`}
            />
          </div>
        </div>
      </section>

    </>
  );
}



